<script setup lang="ts">
import { FAQ } from "@mmh/types"
import { Search, X } from "lucide-vue-next"
import type { HTMLAttributes } from "vue"
import { computed, nextTick, ref } from "vue"
import { ThemeButton, ThemeInput } from "~/components/theme"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "~/components/ui/accordion"
import { useLocation } from "~/composables/locations"
import { cn } from "~/lib/utils"

const { class: cl } = defineProps<{
    class?: HTMLAttributes["class"]
}>()

const { location } = useLocation()
const visibleFAQs = ref(5)
const searchQuery = ref("")

const allFAQs = computed(() => {
    const faqs = location.value?.pageContent?.faqs
    // Handle both legacy array format and new object format
    if (Array.isArray(faqs)) return faqs
    return (faqs as any)?.items || []
})

// Enhanced search functions
interface SearchResult {
    faq: FAQ
    score: number
}

// Function to calculate how relevant a match is
function calculateRelevanceScore(text: string, query: string, isQuestion: boolean): number {
    if (!query.trim()) return 0

    const textLower = text.toLowerCase()
    const queryLower = query.toLowerCase()
    const queryWords = queryLower.split(/\s+/)
    let score = 0

    // Check for exact match with query
    if (textLower === queryLower) {
        return 100 // Maximum score for exact match
    }

    // Check for exact word matches
    for (const word of queryWords) {
        if (word.length < 2) continue // Skip very short words

        // Exact word match with word boundaries
        const wordRegex = new RegExp(`\\b${word}\\b`, "i")
        if (wordRegex.test(textLower)) {
            // Higher score if the word appears in the question
            score += isQuestion ? 50 : 30
        }

        // Contains the word as a substring
        else if (textLower.includes(word)) {
            score += isQuestion ? 25 : 15
        }

        // Check for words that sound similar or have similar spelling
        else if (fuzzySearch(textLower, word)) {
            score += isQuestion ? 10 : 5
        }
    }

    // Boost score if the query appears at start of text
    if (textLower.startsWith(queryLower)) {
        score += 20
    }

    // Higher ranking if the length of the text is closer to query length
    // (avoids overly long text matches with single word hits)
    const lengthRatio = Math.min(queryLower.length / textLower.length, 1)
    score += lengthRatio * 10

    return score
}

// Simple fuzzy search for character sequence matching
function fuzzySearch(text: string, query: string): boolean {
    if (!query.trim()) return true

    const textLower = text.toLowerCase()
    const queryLower = query.toLowerCase()

    // Perform character sequence matching
    let idx = 0
    for (const char of queryLower) {
        idx = textLower.indexOf(char, idx)
        if (idx === -1) return false
        idx += 1
    }

    return true
}

// Filter and rank FAQs based on search query
const filteredFAQs = computed(() => {
    if (!searchQuery.value.trim()) return allFAQs.value

    const results: SearchResult[] = []

    for (const faq of allFAQs.value) {
        // Calculate scores for question and answer separately
        const questionScore = calculateRelevanceScore(faq.question, searchQuery.value, true)
        const answerScore = calculateRelevanceScore(faq.answer, searchQuery.value, false)

        // Use the higher of the two scores, with a bias towards question matches
        const totalScore = Math.max(questionScore, answerScore * 0.8)

        // Only include results with a positive score
        if (totalScore > 0) {
            results.push({
                faq,
                score: totalScore
            })
        }
    }

    // Sort by score (highest first) and return just the FAQ objects
    return results
        .sort((a, b) => b.score - a.score)
        .map(result => result.faq)
})

// Updated to use filteredFAQs instead of allFAQs
const displayedFAQs = computed(() => {
    return searchQuery.value.trim()
        ? filteredFAQs.value
        : filteredFAQs.value.slice(0, visibleFAQs.value)
})


function showMoreFAQs() {
    const firstNewIndex = visibleFAQs.value
    visibleFAQs.value += 5
    nextTick(() => {
        const triggers = accordionRef.value?.$el?.querySelectorAll<HTMLElement>("button")
        triggers?.[firstNewIndex]?.focus()
    })
}

function clearSearch() {
    searchQuery.value = ""
}

const accordionRef = ref<{ $el: HTMLElement } | null>(null)

const canLoadMore = computed(() => {
    return !searchQuery.value.trim() && visibleFAQs.value < allFAQs.value.length
})
</script>

<template>
    <div
        v-if="location"
        id="faq"
        tabindex="-1"
        :class="cn('space-y-10 md:space-y-12 px-4 sm:px-6', cl)"
    >
        <header class="space-y-6 text-center">
            <h2 class="text-2xl sm:text-3xl lg:text-4xl font-headline text-center text-balance max-w-full overflow-hidden">
                Frequently Asked Questions
            </h2>
        </header>
        <div class="space-y-4">
            <div class="group relative text-faq-search-muted w-full md:w-72">
                <span
                    class="absolute top-1/2 -translate-y-1/2 flex items-center justify-center group-focus-within:text-foreground"
                >
                    <Search class="size-4" />
                </span>
                <label
                    for="faq-search"
                    class="sr-only"
                >Search frequently asked questions</label>
                <ThemeInput
                    id="faq-search"
                    name="faq-search"
                    placeholder="Type your question..."
                    type="search"
                    class="text-foreground pl-8 border-0 border-b border-border/50 [&::placeholder]:text-faq-search-placeholder [&::placeholder]:opacity-100 group-focus-within:border-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
                    :model-value="searchQuery"
                    @update:model-value="(v: string | number) => searchQuery = String(v)"
                />
                <ThemeButton
                    v-if="searchQuery.length > 0"
                    variant="ghost"
                    size="icon"
                    class="absolute right-0 top-1/2 -translate-y-1/2 p-0 size-auto hover:bg-transparent"
                    aria-label="Clear search"
                    @click="clearSearch"
                >
                    <X class="size-4" />
                </ThemeButton>
            </div>
            <div
                v-if="filteredFAQs.length === 0 && searchQuery.trim().length > 0"
                class="text-center"
            >
                <p>No FAQs found matching "{{ searchQuery }}"</p>
                <ThemeButton
                    variant="outline"
                    aria-label="Clear Search"
                    tabindex="0"
                    class="border-primary text-primary hover:bg-primary/10"
                    @click="clearSearch"
                >
                    Clear Search
                </ThemeButton>
            </div>
            <Accordion
                v-else
                ref="accordionRef"
                type="single"
                collapsible
                class="w-full"
            >
                <AccordionItem
                    v-for="(faq, idx) in displayedFAQs"
                    :key="idx"
                    :value="idx.toString()"
                    class="border-border/50 "
                >
                    <AccordionTrigger class="text-left font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background">
                        {{ faq.question }}
                    </AccordionTrigger>
                    <AccordionContent>
                        <div v-html="faq.answer" />
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>
        <div class="grid place-items-end">
            <ThemeButton
                v-if="canLoadMore"
                size="sm"
                aria-label="Show More FAQs"
                tabindex="0"
                @click="showMoreFAQs"
            >
                <span>Show More...</span>
            </ThemeButton>
        </div>
    </div>
</template>
